'use client'

import { useCallback, useMemo } from 'react'
import type { IProduct, ISavedModifier } from '@/app/[locale]/_types'
import {
  canAddSimpleProductDirectly,
  fetchDirectCartModifiers,
  getOrderingDeliveryType,
  isOrderingLocationReady,
} from '@/app/[locale]/_utils/modifier/buildCompulsoryModifiers'

interface UseProductQuickAddOptions {
  product: IProduct
  menuId: string
  isHall: boolean
  orderType?: string
  branchId?: string | null
  geolocation?: string
  isOutOfStock?: boolean
  skipLocationCheck?: boolean
  onDirectAdd: (orderModifiers: ISavedModifier[]) => void
  onOpenDetail: () => void
  onOpenMap: () => void
}

export function useProductQuickAdd({
  product,
  menuId,
  isHall,
  orderType,
  branchId,
  geolocation,
  isOutOfStock = false,
  skipLocationCheck = false,
  onDirectAdd,
  onOpenDetail,
  onOpenMap,
}: UseProductQuickAddOptions) {
  const canAddToCartDirectly = useMemo(
    () => canAddSimpleProductDirectly(product, menuId),
    [product, menuId]
  )

  const deliveryType = useMemo(
    () => getOrderingDeliveryType({ isHall, orderType }),
    [isHall, orderType]
  )

  const handleAddClick = useCallback(async () => {
    if (isOutOfStock) return

    if (
      !skipLocationCheck &&
      !isOrderingLocationReady({
        isHall,
        orderType,
        branchId,
        geolocation,
      })
    ) {
      onOpenMap()
      return
    }

    if (canAddToCartDirectly) {
      onDirectAdd([])
      return
    }

    if (product.has_modifier && product.type === 'simple') {
      try {
        const orderModifiers = await fetchDirectCartModifiers(
          product.id,
          menuId,
          deliveryType
        )

        if (orderModifiers?.length) {
          onDirectAdd(orderModifiers)
          return
        }
      } catch {
        // Fall back to product detail when modifier fetch fails.
      }
    }

    onOpenDetail()
  }, [
    branchId,
    canAddToCartDirectly,
    deliveryType,
    geolocation,
    isHall,
    isOutOfStock,
    menuId,
    onDirectAdd,
    onOpenDetail,
    onOpenMap,
    orderType,
    product.has_modifier,
    product.id,
    product.type,
    skipLocationCheck,
  ])

  return { canAddToCartDirectly, handleAddClick }
}
